import React, { useState, useMemo } from 'react';
import { Play, ChevronDown, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ShortsGalleryProps {
  videos: any[];
  onSelectVideo: (id: string) => void;
  onLoadMore: () => void;
  hasMoreFromSupabase: boolean;
}

export default function ShortsGallery({ videos, onSelectVideo, onLoadMore, hasMoreFromSupabase }: ShortsGalleryProps) {
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState<{ [key: string]: boolean }>({});

  // Filtra rigorosamente apenas os shorts e EMBARALHA dinamicamente para nunca ficarem na mesma ordem
  // Filtra rigorosamente apenas os shorts (O embaralhamento agora é feito no Supabase/Pai)
  const shortsVideos = useMemo(() => {
    return videos.filter((v: any) => 
      v.post_type === 'shorts' || v.is_short === true || (v.category || '').toLowerCase().includes('shorts')
    );
  }, [videos]);

  if (shortsVideos.length === 0) {
    return (
      <div style={{ padding: '20px', backgroundColor: '#000', minHeight: '100vh', width: '100%' }}>
        <button 
          onClick={() => navigate('/')} 
          style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', marginBottom: '20px' }}
        >
          <ArrowLeft size={22} /> Voltar ao Início
        </button>
        <div style={{ color: '#fff', height: '70vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
          <h2 style={{ fontSize: '20px', marginBottom: '10px' }}>Nenhum Short encontrado!</h2>
          <p style={{ color: '#888', fontSize: '14px' }}>Carregando galeria de videos.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: '100vh', backgroundColor: '#000', position: 'relative', display: 'flex', justifyContent: 'center' }}>
      
      {/* 📱 CONTEÚDO CENTRAL DA GALERIA DE SHORTS */}
      <div style={{ width: '100%', maxWidth: '1200px', minHeight: '100vh', backgroundColor: '#000', boxSizing: 'border-box', paddingBottom: '60px' }}>
        
        {/* BARRA DE TOPO COM BOTÃO DE VOLTAR E TÍTULO */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px 16px', backgroundColor: '#000', position: 'sticky', top: 0, zIndex: 10, borderBottom: '1px solid #1a1a1a' }}>
          <button 
            onClick={() => navigate('/')}
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.1)', 
              border: 'none', 
              borderRadius: '50%', 
              width: '40px', 
              height: '40px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: 'pointer',
              transition: 'background 0.2s'
            }}
            title="Voltar"
          >
            <ArrowLeft color="#fff" size={22} />
          </button>

          <h2 style={{ color: '#fff', fontSize: '18px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <span style={{ backgroundColor: '#E50914', color: '#fff', fontSize: '11px', padding: '3px 8px', borderRadius: '4px' }}>SHORTS</span>
            Galeria de Vídeos
          </h2>

          <div style={{ width: '40px' }} />
        </div>

        {/* GRADE DE VÍDEOS */}
        <div style={{ padding: '20px 16px' }}>
          <div className="shorts-grid" style={{ display: 'grid', gap: '15px' }}>
            <style>{`
              @keyframes shimmer {
                0% { background-position: -200% 0; }
                100% { background-position: 200% 0; }
              }
              .image-skeleton {
                background: linear-gradient(90deg, #1a1a1a 25%, #2a2a2a 50%, #1a1a1a 75%);
                background-size: 200% 100%;
                animation: shimmer 1.5s infinite;
              }

              .shorts-grid {
                grid-template-columns: repeat(2, 1fr);
              }
              @media (min-width: 768px) {
                .shorts-grid {
                  grid-template-columns: repeat(4, 1fr);
                  gap: 20px;
                }
              }
              @media (min-width: 1024px) {
                .shorts-grid {
                  grid-template-columns: repeat(5, 1fr);
                }
              }
            `}</style>

            {shortsVideos.map((video) => {
              const url = video.embed_id || video.full_video_id || video.trailer_url || video.url || '';
              const ytMatch = url.match(/^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|shorts\/)([^#&?]*).*/);
              const ytId = (ytMatch && ytMatch[2].length === 11) ? ytMatch[2] : '';
              const thumbnail = video.thumbnail || (ytId ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg` : '');
              const isLoaded = loadedImages[video.id];

              return (
                <div 
                  key={video.id}
                  onClick={() => onSelectVideo(video.id)}
                  style={{ 
                    cursor: 'pointer', 
                    borderRadius: '12px', 
                    overflow: 'hidden', 
                    position: 'relative',
                    aspectRatio: '9/16', 
                    backgroundColor: '#111',
                    border: '1px solid #222',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'scale(1.03)';
                    e.currentTarget.style.boxShadow = '0 6px 15px rgba(229, 9, 20, 0.2)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {!isLoaded && (
                    <div className="image-skeleton" style={{ position: 'absolute', inset: 0, zIndex: 1 }} />
                  )}

                  <img 
                    src={thumbnail} 
                    alt={video.title} 
                    loading="lazy"
                    decoding="async"
                    onLoad={() => setLoadedImages(prev => ({ ...prev, [video.id]: true }))}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      objectFit: 'cover',
                      opacity: isLoaded ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      position: 'relative',
                      zIndex: 2
                    }} 
                  />
                  
                  <div style={{
                    position: 'absolute',
                    bottom: 0, left: 0, width: '100%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.95), rgba(0,0,0,0.5), transparent)',
                    padding: '40px 12px 12px 12px',
                    display: 'flex',
                    alignItems: 'flex-end',
                    zIndex: 3
                  }}>
                    <h4 style={{ 
                      color: '#fff', margin: 0, fontSize: '13px', lineHeight: '1.3',
                      display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' 
                    }}>
                      {video.title}
                    </h4>
                  </div>

                  <div style={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '50%', padding: '12px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', zIndex: 4
                  }}>
                    <Play fill="#fff" color="#fff" size={24} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* BOTÃO DE CARREGAR MAIS (Paginado direto no Supabase) */}
          {hasMoreFromSupabase && (
            <div style={{ textAlign: 'center', marginTop: '40px', marginBottom: '40px', width: '100%' }}>
              <button 
                onClick={onLoadMore}
                style={{
                  backgroundColor: '#111', color: '#fff', border: '1px solid #333',
                  padding: '14px 35px', borderRadius: '6px', fontWeight: 'bold', fontSize: '15px', 
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
                  transition: 'background 0.2s', boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#222'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#111'}
              >
                Carregar mais Shorts <ChevronDown size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}